import styled from '@emotion/styled';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '../../../utils/hooks/useMediaQuery';
import { breakpointQueries } from '../../../utils/styleguide';
import Icon from '../../component-library/Icon';
import { NavLinkItemType } from '../../component-library/navigation/navTypes';
import { NavBarServerProps, processLinkGroups } from '../util/navigation';
import ActionNavBarButton from './ActionNavBarButton';

function parseNavBarFields({
  imagePortrait,
  navButton,
  mobileBottomLinks,
  linkGroups,
}: NavBarServerProps) {
  return {
    imagePortrait: imagePortrait,
    navButton: navButton ? <ActionNavBarButton button={navButton} /> : null,
    mobileBottomLinks: processLinkGroups(mobileBottomLinks),
    linkGroups: processLinkGroups(linkGroups),
  };
}

export type Props = {
  navigationBar: NavBarServerProps;
  initiallyTransparent?: boolean;
};

export const useNavBar = ({ navigationBar }: { navigationBar: Props['navigationBar'] }) => {
  const [isDesktop] = useMediaQuery(breakpointQueries.desktop);
  const [isTabletOrDesktop] = useMediaQuery(breakpointQueries.tablet);
  const [hovered, setHovered] = useState(false);
  const [submenu, setSubmenu] = useState<NavLinkItemType | null | undefined>(null);
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (!hovered) {
      setSubmenu(null);
    }
  }, [hovered]);

  useEffect(() => {
    setSubmenu(null);
  }, [router.query]);

  useEffect(() => {
    if (isMenuOpened && !isTabletOrDesktop) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpened, isTabletOrDesktop]);

  const { navButton, linkGroups, mobileBottomLinks } = useMemo(
    () => parseNavBarFields(navigationBar),
    [navigationBar],
  );

  const handleNavItemSelected = useCallback(
    ({ id, type }: { id?: string; type: 'click' | 'focus' }) => {
      if (!id) {
        setSubmenu(null);
        return;
      }

      if (!isDesktop && type === 'focus') {
        return;
      }

      const link = linkGroups.find((link: NavLinkItemType) => link.id === id);

      if (!link) {
        return;
      }

      if (link.titleLink) {
        setIsMenuOpened(false);
        setSubmenu(null);
        return;
      }

      if (!submenu || submenu.id !== id) {
        setSubmenu(linkGroups.find((link: NavLinkItemType) => link.id === id));
        return;
      }

      setSubmenu(null);
    },
    [isDesktop, linkGroups, submenu, setIsMenuOpened],
  );

  const clearSubmenu = useCallback(() => {
    setSubmenu(null);
  }, [setSubmenu]);

  return {
    linkGroups,
    navButton,
    handleNavItemSelected,
    submenu,
    clearSubmenu,
    setHovered,
    mobileBottomLinks,
    isMenuOpened,
    setIsMenuOpened,
  };
};

export type HandleNavItemSelected = ReturnType<typeof useNavBar>['handleNavItemSelected'];

const HomeNavigation = styled.div`
  pointer-events: auto;
`;

const GoHomeLink = styled.a`
  pointer-events: auto;
  display: flex;
  text-decoration: none;
  align-items: center;
`;

const LogoBrandIcon = styled(Icon)`
  height: 24px;
  width: 158px;
  path {
    transition: fill 0.3s ease;
    ${({ color }: { color: string }) => `fill: ${color};`}
  }
`;

export const Home = ({
  textColor,
  handleNavItemClick,
}: {
  textColor: string;
  handleNavItemClick: HandleNavItemSelected;
}) => (
  <HomeNavigation>
    <Link href="/" passHref>
      <GoHomeLink
        aria-label="go-home"
        onClick={() => handleNavItemClick({ type: 'click' })}
        onMouseEnter={() => handleNavItemClick({ type: 'focus' })}
      >
        <LogoBrandIcon name={'milkywire-logo-brand'} color={textColor} size={24} />
      </GoHomeLink>
    </Link>
  </HomeNavigation>
);
